import { getSupabaseClient } from '../../offline/supabase'
import type {
  ByeApplyReceipt,
  ByeRangePreview,
  ByeUndoReceipt,
  GeneralOutcomeBatchInput,
  GeneralOutcomeBatchPreview,
  GeneralOutcomeBatchReceipt,
  GeneralOutcomeBatchUndoReceipt,
  OwnerMaintenanceInput,
  OwnerMaintenancePreview,
  OwnerMaintenanceReceipt,
  OperationWorkflowAdapter,
  OwnerRevision,
  TimelineOperation,
  TimelinePreview,
  TimelineReceipt,
} from './contracts'

export class OwnerWorkflowError extends Error {
  constructor(public readonly code: string, message: string, public readonly status?: number) {
    super(message)
    this.name = 'OwnerWorkflowError'
  }
}

export async function withFreshOwnerRevision<T>(
  refresh: () => Promise<OwnerRevision>,
  action: (revision: OwnerRevision) => Promise<T>,
): Promise<T> {
  return action(await refresh())
}

async function invoke(name: 'owner-timeline' | 'bye-range' | 'outcome-batch' | 'owner-maintenance', body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const client = getSupabaseClient()
  if (!client) throw new Error('Authenticated staging workflows are not configured in this build.')
  const { data, error } = await client.functions.invoke(name, { body })
  if (error) {
    const status = 'context' in error && error.context instanceof Response ? error.context.status : undefined
    let code = status === 409 ? 'conflict' : status === 401 ? 'unauthorized' : 'workflow_rejected'
    if ('context' in error && error.context instanceof Response) {
      try {
        const payload = await error.context.clone().json() as { error?: unknown }
        if (typeof payload.error === 'string') code = payload.error
      } catch { /* The SDK does not guarantee a JSON error body. */ }
    }
    const conflictMessage: Record<string, string> = {
      stale_owner_state: 'Owner data changed after this preview. Reload authoritative data and preview again.',
      owner_state_changed: 'Owner data changed while loading. Reload authoritative data and preview again.',
      outcome_preview_mismatch: 'The exact preview no longer matches. Generate a fresh preview before applying.',
      outcome_cell_changed: 'A previewed cell changed. No batch changes were applied.',
      outcome_cells_changed: 'At least one batch cell changed. The complete undo was rejected without partial changes.',
      outcome_batch_already_undone: 'This batch was already undone by a different request.',
      idempotency_key_reused: 'The request identity was already used for different batch content.',
      outcome_batch_incomplete: 'The earlier batch attempt did not produce a complete receipt. Reload authoritative data.',
      maintenance_preview_mismatch: 'The maintenance preview no longer matches. Generate a fresh exact preview.',
      maintenance_target_changed: 'The selected item changed after preview. Reload authoritative data and review again.',
      maintenance_target_inactive: 'The selected item is already inactive on that date.',
      maintenance_target_active: 'The selected item is already active on that date.',
      delete_backup_required: 'Download the authoritative backup before typing the permanent deletion confirmation.',
      transaction_failed: 'The secured transaction failed without applying a partial batch.',
    }
    const message = status === 409
      ? conflictMessage[code] ?? 'The secured workflow found a conflict. Reload authoritative data before retrying.'
      : status === 401
      ? 'Your session expired. Sign in again before making this change.'
      : error.message || 'The secured owner workflow was rejected.'
    throw new OwnerWorkflowError(code, message, status)
  }
  if (!data || typeof data !== 'object') throw new Error('The secured owner workflow returned an invalid response.')
  return data as Record<string, unknown>
}

function validRevision(data: Record<string, unknown>, expected = false): boolean {
  const structure = expected ? data.expectedStructureVersion : data.structureVersion
  const score = expected ? data.expectedScoreRevision : data.scoreRevision
  return Number.isSafeInteger(structure) && Number.isSafeInteger(score)
}

function validCategoryAdjustment(value: unknown): boolean {
  return value === undefined || value === null || (
    typeof value === 'object' && !Array.isArray(value) &&
    typeof (value as Record<string, unknown>).categorySourceKey === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(String((value as Record<string, unknown>).previousStartDate ?? '')) &&
    /^\d{4}-\d{2}-\d{2}$/.test(String((value as Record<string, unknown>).newStartDate ?? '')) &&
    (value as Record<string, unknown>).reason === 'backdated_new_operation'
  )
}

function timelinePreview(data: Record<string, unknown>): TimelinePreview {
  const impact = data.impact as Record<string, unknown> | undefined
  if (!validRevision(data, true) || !validCategoryAdjustment(data.categoryAdjustment) || !data.operation || !impact || !Array.isArray(impact.changedDays) || !Array.isArray(impact.trails) || !impact.accumulation || !Array.isArray(impact.newlyOutsideScheduleEntries) || !Array.isArray(impact.newlyIncludedEntries) || typeof data.previewFingerprint !== 'string' || typeof data.confirmationText !== 'string') {
    throw new Error('The secured timeline preview returned an invalid response.')
  }
  return { ...data, categoryAdjustment: data.categoryAdjustment ?? null } as unknown as TimelinePreview
}

function timelineReceipt(data: Record<string, unknown>): TimelineReceipt {
  if (!validRevision(data) || !validCategoryAdjustment(data.categoryAdjustment) || typeof data.operationId !== 'string' || typeof data.operationSourceKey !== 'string' || !data.preview) throw new Error('The secured timeline apply returned an invalid response.')
  return { ...data, categoryAdjustment: data.categoryAdjustment ?? null } as unknown as TimelineReceipt
}

function byePreview(data: Record<string, unknown>): ByeRangePreview {
  if (!validRevision(data, true) || typeof data.rangeStart !== 'string' || typeof data.rangeEnd !== 'string' || !Array.isArray(data.selectedOperations) || !Array.isArray(data.cells) || !Array.isArray(data.preserved) || !Number.isSafeInteger(data.skippedOptional) || !Number.isSafeInteger(data.skippedUnscheduled) || typeof data.previewFingerprint !== 'string' || typeof data.confirmationText !== 'string') throw new Error('The secured Bye preview returned an invalid response.')
  return data as unknown as ByeRangePreview
}

function byeApplyReceipt(data: Record<string, unknown>, preview: ByeRangePreview): ByeApplyReceipt {
  const preserved = Array.isArray(data.preserved)
    ? data.preserved
    : data.idempotent === true
    ? preview.preserved
    : null
  if (typeof data.batchId !== 'string' || !Number.isSafeInteger(data.appliedCells) || !Number.isSafeInteger(data.scoreRevision) || typeof data.idempotent !== 'boolean' || preserved === null) throw new Error('The secured Bye apply returned an invalid response.')
  return { ...data, preserved } as ByeApplyReceipt
}

function byeUndoReceipt(data: Record<string, unknown>): ByeUndoReceipt {
  if (typeof data.batchId !== 'string' || !Number.isSafeInteger(data.undoneCells) || !Number.isSafeInteger(data.scoreRevision)) throw new Error('The secured Bye undo returned an invalid response.')
  return data as unknown as ByeUndoReceipt
}

function outcomeBatchPreview(data: Record<string, unknown>): GeneralOutcomeBatchPreview {
  const input = data.input as Record<string, unknown> | undefined
  const counts = data.counts as Record<string, unknown> | undefined
  const impact = data.impact as Record<string, unknown> | undefined
  const countKeys = ['changed', 'skippedExisting', 'skippedEmpty', 'skippedGeneratedBye', 'skippedImportOnly', 'skippedNotDue', 'skippedInactive', 'skippedWeekday']
  if (!validRevision(data, true) || !input || !Array.isArray(input.operationIds) || typeof input.asOf !== 'string' || !(input.note === null || typeof input.note === 'string' && input.note === input.note.trim() && input.note.length <= 500) || !Array.isArray(data.cells) || !counts || countKeys.some((key) => !Number.isSafeInteger(counts[key])) || !impact || !Array.isArray(impact.changedDays) || !Array.isArray(impact.trails) || !impact.accumulation || typeof data.previewFingerprint !== 'string' || !/^[0-9a-f]{64}$/.test(data.previewFingerprint) || typeof data.confirmationText !== 'string') {
    throw new Error('The secured outcome batch preview returned an invalid response.')
  }
  return data as unknown as GeneralOutcomeBatchPreview
}

function outcomeBatchReceipt(data: Record<string, unknown>): GeneralOutcomeBatchReceipt {
  if (!validRevision(data) || typeof data.batchId !== 'string' || !Number.isSafeInteger(data.changedCells) || typeof data.idempotent !== 'boolean' || !(data.note === null || typeof data.note === 'string' && data.note === data.note.trim() && data.note.length <= 500)) throw new Error('The secured outcome batch apply returned an invalid response.')
  return data as unknown as GeneralOutcomeBatchReceipt
}

function outcomeBatchUndoReceipt(data: Record<string, unknown>): GeneralOutcomeBatchUndoReceipt {
  if (!validRevision(data) || typeof data.batchId !== 'string' || !Number.isSafeInteger(data.undoneCells) || typeof data.idempotent !== 'boolean' || !(data.note === null || typeof data.note === 'string' && data.note === data.note.trim() && data.note.length <= 500)) throw new Error('The secured outcome batch undo returned an invalid response.')
  return data as unknown as GeneralOutcomeBatchUndoReceipt
}

const maintenanceActions = ['rename', 'move', 'archive', 'restore', 'delete'] as const
const maintenanceTargetKinds = ['operation', 'category'] as const
const maintenanceChanges = [...maintenanceActions, 'cascade-archive', 'selected-restore'] as const
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/
const sha256Pattern = /^[0-9a-f]{64}$/
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isTrimmedText(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= max && value === value.trim()
}

function isResolvedMaintenanceInput(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const input = value as Record<string, unknown>
  if (!maintenanceActions.includes(input.action as typeof maintenanceActions[number]) ||
      !maintenanceTargetKinds.includes(input.targetKind as typeof maintenanceTargetKinds[number]) ||
      !isTrimmedText(input.targetSourceKey, 300) ||
      !(input.effectiveDate === null || typeof input.effectiveDate === 'string' && isoDatePattern.test(input.effectiveDate)) ||
      !(input.newName === null || isTrimmedText(input.newName, 300)) ||
      !(input.destinationCategorySourceKey === null || isTrimmedText(input.destinationCategorySourceKey, 300)) ||
      !Array.isArray(input.restoreOperationSourceKeys) ||
      input.restoreOperationSourceKeys.some((item) => !isTrimmedText(item, 300)) ||
      new Set(input.restoreOperationSourceKeys).size !== input.restoreOperationSourceKeys.length) return false
  const emptyRestore = input.restoreOperationSourceKeys.length === 0
  if (input.action === 'rename') return input.newName !== null && input.effectiveDate === null && input.destinationCategorySourceKey === null && emptyRestore
  if (input.action === 'move') return input.targetKind === 'operation' && input.newName === null && input.effectiveDate === null && input.destinationCategorySourceKey !== null && emptyRestore
  if (input.action === 'archive') return input.effectiveDate !== null && input.newName === null && input.destinationCategorySourceKey === null && emptyRestore
  if (input.action === 'restore') return input.effectiveDate !== null && input.newName === null && input.destinationCategorySourceKey === null && (input.targetKind === 'category' || emptyRestore)
  return input.effectiveDate === null && input.newName === null && input.destinationCategorySourceKey === null && emptyRestore
}

function isMaintenanceAffectedItems(value: unknown): boolean {
  if (!Array.isArray(value) || value.length < 1) return false
  const identities = new Set<string>()
  for (const raw of value) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false
    const item = raw as Record<string, unknown>
    if (!maintenanceTargetKinds.includes(item.kind as typeof maintenanceTargetKinds[number]) ||
        !isTrimmedText(item.sourceKey, 300) || !isTrimmedText(item.name, 300) ||
        !maintenanceChanges.includes(item.change as typeof maintenanceChanges[number])) return false
    const identity = `${item.kind}:${item.sourceKey}`
    if (identities.has(identity)) return false
    identities.add(identity)
  }
  return true
}

function isHistoricalImpact(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const impact = value as Record<string, unknown>
  return Array.isArray(impact.changedDays) && Array.isArray(impact.trails) && Boolean(impact.accumulation) &&
    Array.isArray(impact.newlyOutsideScheduleEntries) && Array.isArray(impact.newlyIncludedEntries)
}

function isMaintenanceCategoryImpacts(value: unknown): boolean {
  if (!Array.isArray(value)) return false
  const sourceKeys = new Set<string>()
  for (const raw of value) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false
    const item = raw as Record<string, unknown>
    if (!isTrimmedText(item.categorySourceKey, 300) || !isTrimmedText(item.categoryName, 300) || !isHistoricalImpact(item.impact) || sourceKeys.has(item.categorySourceKey)) return false
    sourceKeys.add(item.categorySourceKey)
  }
  return true
}

function maintenancePreview(data: Record<string, unknown>): OwnerMaintenancePreview {
  const input = data.input as Record<string, unknown> | undefined
  const impact = data.impact as Record<string, unknown> | undefined
  const backup = data.backup as Record<string, unknown> | null | undefined
  const validInput = isResolvedMaintenanceInput(input)
  const validImpact = isHistoricalImpact(impact)
  const validBackup = backup === null || Boolean(backup && isTrimmedText(backup.fileName, 300) && typeof backup.sha256 === 'string' && sha256Pattern.test(backup.sha256) && backup.document && typeof backup.document === 'object' && Array.isArray((backup.document as Record<string, unknown>).categories) && Array.isArray((backup.document as Record<string, unknown>).operations) && Array.isArray((backup.document as Record<string, unknown>).entries))
  const backupMatchesAction = validInput && (input.action === 'delete' ? backup !== null : backup === null)
  if (!validRevision(data, true) || !validInput || !validImpact || !isMaintenanceCategoryImpacts(data.categoryImpacts) || !validBackup || !backupMatchesAction || !isTrimmedText(data.targetName, 300) || !isMaintenanceAffectedItems(data.affectedItems) || !Number.isSafeInteger(data.affectedEntryCount) || Number(data.affectedEntryCount) < 0 || typeof data.previewFingerprint !== 'string' || !sha256Pattern.test(data.previewFingerprint) || !isTrimmedText(data.confirmationText, 200)) {
    throw new Error('The secured maintenance preview returned an invalid response.')
  }
  return data as unknown as OwnerMaintenancePreview
}

function maintenanceReceipt(data: Record<string, unknown>): OwnerMaintenanceReceipt {
  const validAction = maintenanceActions.includes(data.action as typeof maintenanceActions[number])
  const validChecksum = data.action === 'delete'
    ? typeof data.backupChecksum === 'string' && sha256Pattern.test(data.backupChecksum)
    : data.backupChecksum === null
  if (!validRevision(data) || typeof data.mutationId !== 'string' || !uuidPattern.test(data.mutationId) || !validAction || !maintenanceTargetKinds.includes(data.targetKind as typeof maintenanceTargetKinds[number]) || !Number.isSafeInteger(data.affectedItems) || Number(data.affectedItems) < 1 || !Number.isSafeInteger(data.affectedEntries) || Number(data.affectedEntries) < 0 || typeof data.idempotent !== 'boolean' || !validChecksum) {
    throw new Error('The secured maintenance apply returned an invalid response.')
  }
  return data as unknown as OwnerMaintenanceReceipt
}

export const supabaseOperationWorkflowAdapter: OperationWorkflowAdapter = {
  previewOwnerTimeline(revision: OwnerRevision, operation: TimelineOperation) {
    return invoke('owner-timeline', {
      action: 'preview', expectedStructureVersion: revision.structureVersion,
      expectedScoreRevision: revision.scoreRevision, operation,
    }).then(timelinePreview)
  },
  applyOwnerTimeline(preview: TimelinePreview) {
    return invoke('owner-timeline', {
      action: 'apply', expectedStructureVersion: preview.expectedStructureVersion,
      expectedScoreRevision: preview.expectedScoreRevision, operation: preview.operation,
      previewFingerprint: preview.previewFingerprint, confirmationText: preview.confirmationText,
    }).then(timelineReceipt)
  },
  previewByeRange(revision, input) {
    return invoke('bye-range', {
      action: 'preview', expectedStructureVersion: revision.structureVersion,
      expectedScoreRevision: revision.scoreRevision, ...input,
    }).then(byePreview)
  },
  applyByeRange(preview, clientBatchId) {
    return invoke('bye-range', {
      action: 'apply', expectedStructureVersion: preview.expectedStructureVersion,
      expectedScoreRevision: preview.expectedScoreRevision, rangeStart: preview.rangeStart,
      rangeEnd: preview.rangeEnd, selectedOperations: preview.selectedOperations, note: preview.note,
      previewFingerprint: preview.previewFingerprint, confirmationText: preview.confirmationText,
      clientBatchId,
    }).then((data) => byeApplyReceipt(data, preview))
  },
  undoByeRange(batchId, clientMutationId) {
    return invoke('bye-range', { action: 'undo', batchId, clientMutationId }).then(byeUndoReceipt)
  },
  previewOutcomeBatch(revision: OwnerRevision, input: GeneralOutcomeBatchInput) {
    return invoke('outcome-batch', {
      action: 'preview', expectedStructureVersion: revision.structureVersion,
      expectedScoreRevision: revision.scoreRevision, input,
    }).then(outcomeBatchPreview)
  },
  applyOutcomeBatch(preview, clientBatchId) {
    return invoke('outcome-batch', {
      action: 'apply', expectedStructureVersion: preview.expectedStructureVersion,
      expectedScoreRevision: preview.expectedScoreRevision, input: preview.input,
      previewFingerprint: preview.previewFingerprint, confirmationText: preview.confirmationText,
      clientBatchId,
    }).then(outcomeBatchReceipt)
  },
  undoOutcomeBatch(revision, batchId, clientMutationId) {
    return invoke('outcome-batch', {
      action: 'undo', expectedStructureVersion: revision.structureVersion,
      expectedScoreRevision: revision.scoreRevision, batchId, clientMutationId,
    }).then(outcomeBatchUndoReceipt)
  },
  previewOwnerMaintenance(revision: OwnerRevision, input: OwnerMaintenanceInput) {
    return invoke('owner-maintenance', {
      action: 'preview', expectedStructureVersion: revision.structureVersion,
      expectedScoreRevision: revision.scoreRevision, input,
    }).then(maintenancePreview)
  },
  applyOwnerMaintenance(preview, clientMutationId, confirmation = {}) {
    return invoke('owner-maintenance', {
      action: 'apply', expectedStructureVersion: preview.expectedStructureVersion,
      expectedScoreRevision: preview.expectedScoreRevision, input: preview.input,
      previewFingerprint: preview.previewFingerprint, confirmationText: preview.confirmationText,
      clientMutationId, typedName: confirmation.typedName,
      backupChecksum: confirmation.backupChecksum ?? preview.backup?.sha256 ?? null,
      backupDownloadInitiated: confirmation.backupDownloadInitiated ?? false,
    }).then(maintenanceReceipt)
  },
}
